/**
 * French URL slugs for the /services/:slug hubs.
 *
 * EN URL:  /services/security-guards
 * FR URL:  /fr/services/agents-de-securite
 *
 * Data in src/data/services.js stays keyed by the EN slug; the router maps
 * FR slugs back to that key. Old /fr/services/<en-slug> URLs 301 in
 * vercel.json and client-redirect in App.jsx so nothing breaks.
 */
import { HUBS, SERVICE_HUBS, cityServicePath, resolveCityPath } from "./cityServiceSlugMap";

export const SERVICE_FR_SLUGS = {
  "security-guards":             "agents-de-securite",
  "event-security":              "securite-evenementielle",
  "residential-security":        "securite-residentielle",
  "business-security":           "securite-entreprise",
  "traffic-control":             "controle-de-la-circulation",
  "fire-watch-security":         "surveillance-incendie",
  "hospitality-security":        "securite-hoteliere",
  "luxury-security":             "securite-vip",
  "loss-prevention":             "prevention-des-pertes",
  "crisis-intervention-security":"agents-intervention",
  "construction-site-security":  "securite-chantier",
  "patrol-services":             "patrouille-mobile",
  "commercial-properties":       "securite-commerciale",
  "education-security":          "securite-etablissements-scolaires",
  "government-services":         "securite-gouvernementale",
  "security-systems":            "systemes-de-securite",
  "security-staffing":           "solutions-de-personnel",
  "hybrid-security-solutions":   "solutions-hybrides",
  "transportation-security":     "securite-du-transport",
};

const FR_TO_EN = Object.fromEntries(
  Object.entries(SERVICE_FR_SLUGS).map(([en, fr]) => [fr, en])
);

/** EN data key → FR URL slug (falls back to the EN slug if unmapped). */
export function toFrServiceSlug(enSlug) {
  return SERVICE_FR_SLUGS[enSlug] || enSlug;
}

/** Any URL slug (EN or FR) → EN data key, or null if unknown. */
export function resolveServiceKey(urlSlug) {
  if (SERVICE_FR_SLUGS[urlSlug]) return urlSlug;
  return FR_TO_EN[urlSlug] || null;
}

/** Path for a service hub in the requested language. */
export function servicePath(enSlug, isFR) {
  return isFR ? `/fr/services/${toFrServiceSlug(enSlug)}` : `/services/${enSlug}`;
}

/**
 * A city hub or city + service path in the target language (+ its ?query/#hash),
 * or null when `path` isn't a city page. City + service links go to the real
 * page in that language (cityServiceSlugMap: CMS page or published catch-all
 * page, e.g. /montreal/security-guards → /fr/montreal/agents-de-securite);
 * when there's none, to the service hub covering it, else to the city hub.
 */
function localizeCityPath(lang, path) {
  const target = resolveCityPath(path);
  if (!target) return null;
  const isFR = lang === "fr";
  const suffix = (path.match(/[?#].*$/) || [""])[0];
  const hub = HUBS[target.cityKey];
  if (!target.serviceKey) return hub ? hub[lang] + suffix : null;
  const page = cityServicePath(lang, target.cityKey, target.serviceKey);
  if (page) return page + suffix;
  const serviceHub = SERVICE_HUBS[target.serviceKey];
  if (serviceHub) return servicePath(serviceHub, isFR);
  return hub ? hub[lang] : null;
}

/**
 * Localize any internal path for the target language.
 * - FR: prefix /fr and swap /services/<en> → /services/<fr>
 * - EN: strip /fr and swap /services/<fr> → /services/<en>
 * - City hubs and city + service pages: their real address in that language
 *   (see localizeCityPath).
 * External URLs and hashes are returned untouched.
 */
export function localizePath(lang, path) {
  if (!path || /^https?:\/\//i.test(path) || path.startsWith("#")) return path;
  const isFR = lang === "fr";

  const cityPath = localizeCityPath(lang, path);
  if (cityPath) return cityPath;

  let p = path.replace(/^\/fr(?=\/|$)/, "");
  if (!p.startsWith("/")) p = `/${p}`;

  const m = p.match(/^\/services\/([a-z0-9-]+)(\/.*|[?#].*)?$/i);
  if (m) {
    const key = resolveServiceKey(m[1]) || m[1];
    const slug = isFR ? toFrServiceSlug(key) : key;
    p = `/services/${slug}${m[2] || ""}`;
  }

  if (!isFR) return p;
  return p === "/" ? "/fr" : `/fr${p}`;
}
